/* @generated */
import * as React from "react";
import { THEME_CLASSES, GENERIC_DARK_MODE_CLASSNAME } from "../design-tokens/dist/_constants/theme_classes.esm.js";
import { FOUNDATION_COLOR_TOKENS } from "../design-tokens/dist/foundation/tokens.esm.js";
import { ThemeContext } from "./context.esm.js";
import { getThemeClassname, getThemeInfo, getInverseMode, defaultTheme } from "./utils.esm.js";
import { getKeys } from "../config/utils.esm.js";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { vars } from "../config/variables.esm.js";
const deprecatedDarkModeSelector = THEME_CLASSES.vis2020.modes.dark.selector;
const genericDarkModeSelector = `.${GENERIC_DARK_MODE_CLASSNAME}`;
const matchingDarkModeSelectors = [deprecatedDarkModeSelector, genericDarkModeSelector];
const getGlobalMode = () => {
  if (typeof document === "undefined") {
    return "bright";
  }
  const html = document.documentElement;
  return html.matches(matchingDarkModeSelectors.join(",")) ? "dark" : "bright";
};
const getGlobalTheme = () => {
  if (typeof document === "undefined") {
    return defaultTheme;
  }
  const html = document.documentElement;
  return html.classList.contains(THEME_CLASSES.vis2023.baseClassName) ? "vis2023" : "vis2020";
};
const useGlobalThemeAndMode = (observeChanges = true) => {
  const [globalMode, setGlobalMode] = React.useState(getGlobalMode());
  const [globalTheme, setGlobalTheme] = React.useState(getGlobalTheme());
  React.useEffect(() => {
    function handleHtmlChange() {
      if (globalMode !== getGlobalMode()) {
        setGlobalMode(getGlobalMode());
      }
      if (globalTheme !== getGlobalTheme()) {
        setGlobalTheme(getGlobalTheme());
      }
    }
    if (observeChanges) {
      const observer = new MutationObserver(handleHtmlChange);
      observer.observe(document.documentElement, { attributes: true });
      handleHtmlChange();
      return () => {
        observer.disconnect();
      };
    }
  }, [globalMode, globalTheme, observeChanges]);
  return { globalMode, globalTheme };
};
const useTheme = () => {
  const {
    theme: themeFromContext,
    mode: modeFromContext,
    additionalClass,
    overrides: overridesFromContext
  } = React.useContext(ThemeContext);
  const hasContext = !!(themeFromContext && modeFromContext);
  const { globalTheme, globalMode } = useGlobalThemeAndMode(!hasContext);
  const theme = themeFromContext || globalTheme || defaultTheme;
  const mode = modeFromContext || globalMode;
  const className = getThemeClassname({ theme, mode, additionalClass });
  const inverseClassName = getThemeClassname({ theme, mode, isInverse: true, additionalClass });
  const styles = useAssignThemeOverrideInlineVars({ themeOverrides: overridesFromContext, mode });
  const inverseStyles = useAssignThemeOverrideInlineVars({
    themeOverrides: overridesFromContext,
    mode,
    isInverse: true
  });
  const themeProps = React.useMemo(
    () => getThemeInfo({ theme, mode, className, inverseClassName, styles, inverseStyles }),
    [theme, mode, className, inverseClassName, styles, inverseStyles]
  );
  return themeProps;
};
function colorIsFoundationToken(color) {
  return color.startsWith("Foundation");
}
const useAssignThemeOverrideInlineVars = ({
  themeOverrides,
  mode,
  isInverse = false
}) => {
  return React.useMemo(() => {
    if (!themeOverrides || !mode || themeOverrides === "reset") return;
    const { colors } = themeOverrides;
    let styles = {};
    for (const key of getKeys(colors)) {
      const colorsInModes = colors[key];
      if (!colorsInModes) continue;
      let color = colorsInModes[isInverse ? getInverseMode(mode) : mode];
      if (colorIsFoundationToken(color)) {
        color = FOUNDATION_COLOR_TOKENS[color];
      }
      const token = vars.Color[key];
      if (color) {
        styles = {
          ...styles,
          ...assignInlineVars({
            [token]: color
          })
        };
      }
    }
    return styles;
  }, [themeOverrides, mode, isInverse]);
};
export {
  useAssignThemeOverrideInlineVars,
  useTheme
};
